import React from 'react';
import clsx from 'clsx';
import { getInitials, getColorFromName } from './Avatar';
import { CANDIDATE_AVATAR_PLACEHOLDER } from './candidatePlaceholder';
import { isStandInAvatar } from './avatarSource';

export interface CandidateCardAvatarProps {
  id?: string;
  name: string;
  src?: string | null;
  /** Anonymised candidate. Ignored unless a real picture is what rendered. */
  blur?: boolean;
  /** Rendered box size in px, used to scale the initials. Ignored when `fallbackClassName` is set. */
  size?: number;
  /** The variant's own avatar-image class, so the fallback keeps the same box. */
  className?: string;
  blurClassName?: string;
  /** Variants that already style their own initials tile pass it here instead of taking the inline treatment. */
  fallbackClassName?: string;
}

/**
 * The avatar on a single-candidate card.
 *
 * These cards are screenshotted into `card_image`, so whatever renders here is
 * frozen onto the public page. They used to fall back to a hardcoded grey
 * silhouette, which meant a candidate showing initials everywhere else in the app
 * was baked into their card as a generic icon. Initials are the app-wide default
 * for a candidate with no image, so the card follows suit.
 *
 * The name shown beside the avatar is the same one the initials come from, so
 * this reveals nothing extra for anonymised candidates.
 */
export function CandidateCardAvatar({
  id,
  name,
  src,
  blur = false,
  size = 150,
  className,
  blurClassName,
  fallbackClassName,
}: CandidateCardAvatarProps) {
  // Tracking the failed src (not a boolean) lets a later src try again instead
  // of being stuck on the fallback.
  const [erroredSrc, setErroredSrc] = React.useState<string | null>(null);
  const usableSrc = src && src !== erroredSrc ? src : null;

  if (usableSrc || !name) {
    const imageSrc = usableSrc || CANDIDATE_AVATAR_PLACEHOLDER;

    return (
      <img
        id={id}
        src={imageSrc}
        alt={name}
        className={clsx(className, blur && !isStandInAvatar(imageSrc) && blurClassName)}
        onError={() => setErroredSrc(src ?? null)}
      />
    );
  }

  if (fallbackClassName) {
    return (
      <div id={id} className={fallbackClassName} role="img" aria-label={name}>
        {getInitials(name)}
      </div>
    );
  }

  return (
    <div
      id={id}
      className={className}
      role="img"
      aria-label={name}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: getColorFromName(name),
        color: '#fff',
        fontSize: Math.round(size * 0.4),
        fontWeight: 500,
        lineHeight: 1,
      }}
    >
      {getInitials(name)}
    </div>
  );
}
